"use client";

import { useRef, useState } from "react";

import { PauseIcon } from "@/components/icons/pause.icon";
import { PhoneIcon } from "@/components/icons/phone.icon";
import { PlayIcon } from "@/components/icons/play.icon";
import { formatUsPhoneNumber } from "@/components/phoneInput/phoneInput.component";

import type { GetHomePageQueryResult } from "@/sanity.types";

const PLAY_LABEL = "Play background video";
const PAUSE_LABEL = "Pause background video";

export const AdtVideoHero = ({ block }: { block: VideoHeroBlock }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
      return;
    }

    setIsPlaying(true);
    void video.play().catch(() => setIsPlaying(false));
  };

  const phoneNumber = block.phoneNumber ?? "";

  return (
    <section
      data-testid="adt-video-hero"
      className="bg-greyscale-headline relative isolate flex min-h-[560px] w-full items-end overflow-hidden text-white sm:min-h-[620px] lg:min-h-[690px]">
      <video
        ref={videoRef}
        data-testid="adt-video"
        src={block.video.asset?.url ?? undefined}
        autoPlay
        loop
        muted
        playsInline
        aria-hidden
        className="absolute inset-0 -z-20 h-full w-full object-cover"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(0,0,0,0.52)_0%,rgba(0,0,0,0.3)_46%,rgba(0,0,0,0.08)_100%)]"
      />

      <div className="container w-full px-6 pb-10 sm:px-10 sm:pb-14 lg:px-14 lg:pb-16">
        <div className="max-w-4xl">
          <h1 className="headline-2 text-white">{block.title}</h1>
          <a
            href={`tel:${phoneNumber.replace(/\D/g, "")}`}
            className="text-secondary mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-lg font-bold no-underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
            <PhoneIcon width={22} height={22} aria-hidden />
            {formatUsPhoneNumber(phoneNumber) || phoneNumber}
          </a>
        </div>
      </div>

      <button
        type="button"
        onClick={togglePlayback}
        aria-label={isPlaying ? PAUSE_LABEL : PLAY_LABEL}
        className="text-greyscale-headline hover:bg-greyscale-background absolute right-6 bottom-10 flex h-12 w-12 items-center justify-center rounded-full bg-white transition focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:right-10 sm:bottom-14 lg:right-14 lg:bottom-16">
        {isPlaying ? (
          <PauseIcon aria-hidden width={20} height={20} />
        ) : (
          <PlayIcon aria-hidden width={20} height={20} />
        )}
      </button>
    </section>
  );
};

type VideoHeroBlock = Extract<
  NonNullable<NonNullable<GetHomePageQueryResult>["pageBuilder"]>[number],
  { _type: "adtVideoHero" }
>;
